import { History } from "lucide-react";

import {
	ReviewableFinding,
	changedFindingFields,
	feedbackDecisionLabel,
	feedbackReasonLabel,
	findingSeverityLabel,
	findingStatusLabel,
	formatPersonWithOrganization,
	formatReviewDateTime,
	isFindingEditedByReviewer,
	isFindingRevertedToOllie,
	latestApplicantFeedback,
	latestReviewerChange,
	ollieValuesOf
} from "utils/plan-review-finding-review";

/**
 * Small marks under a finding row of the report: what the AHJ reviewer changed ("Pass (was Fail)",
 * "Severity: High"), who changed it and when, and the latest applicant feedback. Renders nothing
 * when nobody changed or rated the finding.
 */
export const PlanReviewFindingReviewMarks = ({ finding }: { finding: ReviewableFinding }) => {
	const isEdited = isFindingEditedByReviewer(finding);
	const isReverted = isFindingRevertedToOllie(finding);
	const reviewerChange = latestReviewerChange(finding);
	const applicantFeedback = latestApplicantFeedback(finding);
	const changedFields = changedFindingFields(finding);
	const ollieValues = ollieValuesOf(finding);

	if (!isEdited && !isReverted && !applicantFeedback) return null;

	const applicantReason = feedbackReasonLabel(applicantFeedback?.reason);

	return (
		<span className="prfrm">
			{(changedFields.status || changedFields.severity) && (
				<span className="prfrm-badges">
					{changedFields.status && (
						<span className="prfrm-badge-secondary">
							{findingStatusLabel(finding.status)} (was {findingStatusLabel(ollieValues.status)})
						</span>
					)}
					{changedFields.severity && (
						<span className="prfrm-badge-outline">Severity: {findingSeverityLabel(finding.severity) ?? "None"}</span>
					)}
				</span>
			)}
			{isEdited && reviewerChange && (
				<span className="prfrm-updated">
					Updated by {formatPersonWithOrganization(reviewerChange.user_name, reviewerChange.organization_name)} ·{" "}
					{formatReviewDateTime(reviewerChange.created_at)}
				</span>
			)}
			{isReverted && <span className="prfrm-updated">Reverted to Ollie’s original</span>}
			{applicantFeedback && (
				<span className="prfrm-applicant">
					<span className="prfrm-badge-outline">Applicant: {feedbackDecisionLabel(applicantFeedback.decision)}</span>
					{applicantReason && <span>{applicantReason}</span>}
					{applicantFeedback.comment && <span>· “{applicantFeedback.comment}”</span>}
					<span className="prfrm-applicant-meta">
						· {formatPersonWithOrganization(applicantFeedback.user_name, applicantFeedback.organization_name)} ·{" "}
						{formatReviewDateTime(applicantFeedback.created_at)}
					</span>
				</span>
			)}
		</span>
	);
};

/** Pill with the number of history records of a finding, for example "3" or "3 updates". */
export const PlanReviewFindingHistoryCount = ({ count, label }: { count: number; label?: string }) => (
	<span className="prfrm-history-count" title={`${count} history records`}>
		<History size={12} />
		{count}
		{label ? ` ${label}` : ""}
	</span>
);
